import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '../../../environments/environment';

type TelemetryItemWithUri = {
  baseType?: unknown;
  name?: unknown;
  baseData?: {
    uri?: unknown;
  } & Record<string, unknown>;
};

@Injectable({ providedIn: 'root' })
export class TelemetryService {
  private readonly client: ApplicationInsights | null;
  private readonly criticalOnlyTelemetry = true;
  private readonly exceptionDedupeWindowMs = 30_000;
  private readonly recentExceptionKeys = new Map<string, number>();

  constructor() {
    const connectionString = (
      environment as { applicationInsightsConnectionString?: string }
    ).applicationInsightsConnectionString?.trim();

    if (!connectionString) {
      this.client = null;
      return;
    }

    const appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false,
        disableAjaxTracking: true,
        disableFetchTracking: true,
        disableCorrelationHeaders: true,
        autoTrackPageVisitTime: false,
      },
    });

    appInsights.loadAppInsights();

    appInsights.addTelemetryInitializer((item) => {
      this.sanitizeTelemetryUrl(item as TelemetryItemWithUri);
      return this.shouldSendTelemetry(item as TelemetryItemWithUri);
    });

    this.client = appInsights;
  }

  trackEvent(name: string, properties?: Record<string, string>): void {
    if (this.criticalOnlyTelemetry) {
      return;
    }

    if (!this.client) {
      return;
    }

    this.client.trackEvent({
      name,
      properties,
    });
  }

  trackException(error: unknown, properties?: Record<string, string>): void {
    if (!this.client) {
      return;
    }

    const { exception, extractedProperties } = this.normalizeException(error);
    const mergedProperties = {
      ...extractedProperties,
      ...(properties ?? {}),
    };

    if (!this.shouldTrackException(exception, mergedProperties)) {
      return;
    }

    this.client.trackException({
      exception,
      properties: mergedProperties,
    });
  }

  private shouldSendTelemetry(item: TelemetryItemWithUri): boolean {
    if (!this.criticalOnlyTelemetry) {
      return true;
    }

    return item.baseType === 'ExceptionData';
  }

  private shouldTrackException(exception: Error, properties: Record<string, string>): boolean {
    const signature = [properties['source'] ?? '', properties['message'] ?? '', exception.message].join('|');
    const now = Date.now();

    const lastSeenAt = this.recentExceptionKeys.get(signature);
    if (lastSeenAt && now - lastSeenAt < this.exceptionDedupeWindowMs) {
      return false;
    }

    this.recentExceptionKeys.set(signature, now);

    // Keep map bounded to avoid unbounded growth during long sessions.
    if (this.recentExceptionKeys.size > 200) {
      for (const [key, seenAt] of this.recentExceptionKeys) {
        if (now - seenAt > this.exceptionDedupeWindowMs) {
          this.recentExceptionKeys.delete(key);
        }
      }
    }

    return true;
  }

  private normalizeException(error: unknown): {
    exception: Error;
    extractedProperties: Record<string, string>;
  } {
    if (error instanceof Error) {
      return {
        exception: error,
        extractedProperties: {},
      };
    }

    if (typeof error === 'string') {
      return {
        exception: new Error(error),
        extractedProperties: { nonErrorExceptionType: 'string' },
      };
    }

    if (typeof error === 'object' && error !== null) {
      const objectError = error as Record<string, unknown>;
      const message = this.resolveObjectErrorMessage(objectError);

      return {
        exception: new Error(message),
        extractedProperties: {
          nonErrorExceptionType: this.resolveObjectType(objectError),
          errorCode: this.toStringValue(objectError['errorCode']),
          errorMessage: this.toStringValue(objectError['errorMessage']),
        },
      };
    }

    return {
      exception: new Error(String(error)),
      extractedProperties: {
        nonErrorExceptionType: typeof error,
      },
    };
  }

  private resolveObjectErrorMessage(error: Record<string, unknown>): string {
    const messageCandidates = [
      error['errorMessage'],
      error['message'],
      error['error_description'],
      error['error'],
      error['errorCode'],
    ];

    for (const candidate of messageCandidates) {
      const stringValue = this.toStringValue(candidate);
      if (stringValue) {
        return stringValue;
      }
    }

    return 'Non-Error exception object';
  }

  private resolveObjectType(error: Record<string, unknown>): string {
    const ctor =
      typeof error.constructor === 'function' && 'name' in error.constructor
        ? (error.constructor.name as string)
        : 'Object';

    return ctor || 'Object';
  }

  private toStringValue(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    return '';
  }

  private sanitizeTelemetryUrl(item: TelemetryItemWithUri): void {
    if (!item.baseData || !('uri' in item.baseData)) {
      return;
    }

    const uri = item.baseData.uri;

    if (typeof uri !== 'string') {
      return;
    }

    try {
      const url = new URL(uri);
      item.baseData.uri = `${url.origin}${url.pathname}`;
    } catch {
      // Ignore invalid telemetry URLs.
    }
  }
}


